import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../../services/axiosConfig';
import { branding } from '../../constants/branding';

interface AirdropRow {
  recipientId: number;
  campaignId?: number;
  campaignName?: string;
  deliveryMode?: string;
  coinSymbol?: string;
  coinName?: string;
  contractAddress?: string;
  blockchain?: string;
  amount?: number | string;
  walletAddress?: string;
  status?: string;
  txHash?: string;
  sentAt?: string;
  claimable?: boolean;
}

const RetailPlatformAirdrops: React.FC = () => {
  const [rows, setRows] = useState<AirdropRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/retail/airdrops');
      const data = response.data;
      if (data?.status === 'success' || data?.success) {
        setRows(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data?.message || 'Failed to load airdrops');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load airdrops');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const claim = async (recipientId: number) => {
    if (!window.confirm('Claim this airdrop to your platform wallet?')) return;
    setClaimingId(recipientId);
    setError(null);
    setSuccess(null);
    try {
      const response = await axiosInstance.post(`/api/retail/airdrops/${recipientId}/claim`);
      const data = response.data;
      if (data?.status === 'success' || data?.success) {
        setSuccess('Claim submitted — check status and tx hash below');
        await load();
      } else {
        setError(data?.message || 'Claim failed');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Claim failed');
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
        border: '1px solid #e5e7eb',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: branding.primaryColor || '#086afb' }}>
          Platform coin airdrops
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: '#ff717122', color: '#b42318', fontSize: 13 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: '#0dca9d22', color: '#0a7a5e', fontSize: 13 }}>
          {success}
        </div>
      )}

      {rows.length === 0 && !loading && (
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>No platform airdrops for your account yet.</p>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                <th style={{ padding: '8px 6px' }}>Campaign</th>
                <th style={{ padding: '8px 6px' }}>Coin</th>
                <th style={{ padding: '8px 6px' }}>Amount</th>
                <th style={{ padding: '8px 6px' }}>Status</th>
                <th style={{ padding: '8px 6px' }}>Tx</th>
                <th style={{ padding: '8px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.recipientId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 6px' }}>{r.campaignName || r.campaignId}</td>
                  <td style={{ padding: '8px 6px' }}>{r.coinSymbol || '—'}</td>
                  <td style={{ padding: '8px 6px' }}>{String(r.amount ?? '—')}</td>
                  <td style={{ padding: '8px 6px' }}>{r.status}</td>
                  <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 11 }}>
                    {r.txHash || '—'}
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    {r.claimable && (
                      <button
                        type="button"
                        disabled={claimingId === r.recipientId}
                        onClick={() => claim(r.recipientId)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 999,
                          border: 'none',
                          background: branding.primaryColor || '#086afb',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        {claimingId === r.recipientId ? 'Claiming…' : 'Claim'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RetailPlatformAirdrops;
